import { MagnifyingGlassIcon } from '@radix-ui/react-icons';
import { Box, Flex, Skeleton } from '@radix-ui/themes';
import { CommonSearchInput } from 'components/common/form/search';
import { CommonSelectInput } from 'components/common/form/select';
import { CommonSortSelect } from 'components/common/form/sort-select';
import { CommonTextInput } from 'components/common/form/text';
import { PLPitchMetricsControls } from 'components/sections/pitch-list/list/pitch-metrics-controls';
import usePitchListStore from 'components/sections/pitch-list/store/use-pitch-list-store';
import { CheckAllButton } from 'contexts/layout/checked-context/select-all-button';
import { CheckedContext } from 'contexts/layout/checked.context';
import { PITCH_TYPE_FILTER_OPTIONS } from 'enums/filters';
import useLatest from 'hooks/useLatestRef';
import { PLSortMode } from 'lib_ts/enums/pitch-list.enums';
import { PitchType } from 'lib_ts/enums/pitches.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IFilterPitches } from 'lib_ts/interfaces/pitches/i-filter-pitches';
import { debounce, first } from 'lodash';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

interface IProps {
  loading?: boolean;
}

export const PitchListToolbar = (props: IProps) => {
  const loading = usePitchListStore((s) => s.loading);
  const sortMode = usePitchListStore((s) => s.sortMode);
  const filters = usePitchListStore((s) => s.filters);
  const pitches = usePitchListStore((s) => s.pitches);

  const [name, setName] = useState(filters.name || '');

  const { resetChecked } = useContext(CheckedContext);

  const performFilter = (value: Partial<IFilterPitches>) => {
    resetChecked();
    usePitchListStore.getState().mergeFilters(value);
  };

  const latestPerformFilter = useLatest(performFilter);
  const onSearch = useCallback(debounce(latestPerformFilter.current, 500), []);

  useEffect(() => {
    onSearch({ name: name });
  }, [name, onSearch]);

  // clear the toolbar when moving between pitch lists
  useEffect(() => {
    return () => {
      onSearch.cancel();
      usePitchListStore.getState().resetFilters();
    };
  }, []);

  // in case we set filters from outside the toolbar (e.g. reset filters button whnen showing no results)
  useEffect(() => {
    setName(filters.name || '');
  }, [filters.name]);

  const pitchIDs = useMemo(() => pitches.map((p) => p._id), [pitches]);

  // TODO: Verify that we can scroll properly on touch devices
  return (
    <Flex
      direction="column"
      gap={RADIX.FLEX.GAP.SM}
      position="relative"
      style={{ scrollbarWidth: 'none' }}
      overflowX="auto"
      minHeight="32px"
    >
      <Flex
        gap={RADIX.FLEX.GAP.SM}
        position="absolute"
        minWidth="768px"
        width="100%"
      >
        <Skeleton loading={!!props.loading}>
          <CheckAllButton
            ids={pitchIDs}
            className="CheckAllButton--pitchList"
          />
        </Skeleton>

        <Skeleton loading={!!props.loading}>
          <Box flexGrow="1">
            <CommonSortSelect
              identifier="PLSortSelect"
              label="Sort Options"
              value={sortMode}
              onChange={(v) => usePitchListStore.getState().setSortMode(v)}
              className="width-100p"
              variant={RADIX.BUTTON.VARIANT.SECONDARY}
              size={RADIX.BUTTON.SIZE.TABLE}
              color={RADIX.COLOR.NEUTRAL}
              options={Object.values(PLSortMode).map((v) => ({
                label: v,
                value: v,
              }))}
            />
          </Box>
        </Skeleton>

        <Skeleton loading={!!props.loading}>
          <Box flexGrow="1">
            <CommonTextInput
              id="pitch-list-search-name"
              name="name"
              placeholder="common.search-placeholder"
              value={name}
              disabled={loading}
              onChange={(v = '') => setName(v)}
              rightSlot={<MagnifyingGlassIcon />}
            />
          </Box>
        </Skeleton>

        <Skeleton loading={!!props.loading}>
          <Box flexGrow="1">
            <CommonSelectInput
              id="pitch-list-training-status"
              placeholder="common.status"
              options={[
                {
                  label: 'pl.trained',
                  value: 'true',
                },
                {
                  label: 'pl.untrained',
                  value: 'false',
                },
              ]}
              value={filters.trained?.toString()}
              onOptionalBooleanChange={(v) =>
                latestPerformFilter.current({
                  trained: v ?? null,
                })
              }
              skipSort
              optional
            />
          </Box>
        </Skeleton>

        <Skeleton loading={!!props.loading}>
          <Box flexGrow="1">
            <CommonSearchInput
              id="pitch-list-search-type"
              name="type"
              placeholder="common.pitch-type"
              options={PITCH_TYPE_FILTER_OPTIONS}
              values={filters.type ? [filters.type] : []}
              onChange={(v) =>
                latestPerformFilter.current({
                  type: (first(v) as PitchType) ?? null,
                })
              }
              disabled={loading}
              optional
              skipSort
            />
          </Box>
        </Skeleton>

        <Skeleton loading={!!props.loading}>
          <Box flexGrow="1">
            <PLPitchMetricsControls />
          </Box>
        </Skeleton>
      </Flex>
    </Flex>
  );
};
